import { last } from 'lodash';
import { DateTime } from 'luxon';

import { LoanSnapshotConfig, PaymentDue, PaymentFrequency, Temporal } from '@willow/types-iso';

import { getActiveOrCompletedWorkoutPlanForPaymentDue } from '../loan/getActiveOrCompletedWorkoutPlanForPaymentDue';

/*
 * Returns next payment due date regardless of transfer status.
 * Lenders want to see the next due date on the dashboard / right rail / etc even if the loan is transferring.
 * Don't use this for calculating next payments due. Use getNextPaymentDueDate instead.
 */
export function getNextPaymentDueDateRegardlessOfTransferStatus(
  snapshotConfig: LoanSnapshotConfig,
  maturityDate: Date,
  paymentFrequency: PaymentFrequency,
): Date | undefined {
  const { loanStatus } = snapshotConfig;
  if (loanStatus === 'paidOff' || loanStatus === 'archived') return undefined;

  let nextPaymentDueDate: Date | undefined;

  // find the first payment due that is not fulfilled or deferred
  const eligiblePaymentDue = snapshotConfig.paymentsDue.find(
    (paymentDue) =>
      !paymentDue.status.fulfilled &&
      // Pre-petition arrears are cured by the trustee, so they don't drive the borrower's next due date
      !paymentDue.status.isPrePetition &&
      // Statements are not considered past due if they're deferred
      // Statements ARE considered past due if they're on other workout plans
      (!paymentDue.status.dWorkoutPlan?.type ||
        !['paymentDeferment', 'paymentDeferral'].includes(paymentDue.status.dWorkoutPlan.type)),
  );

  if (eligiblePaymentDue) {
    nextPaymentDueDate = eligiblePaymentDue.paymentDate;
  } else {
    const latestPaymentDue = last(snapshotConfig.paymentsDue);
    if (latestPaymentDue?.paymentDate) {
      // we will show the next month before statement is generated
      const monthsToAdd = 12 / paymentFrequency;
      const nextPeriod = DateTime.fromJSDate(latestPaymentDue.paymentDate).plus({ months: monthsToAdd });
      // leave nextPaymentDueDate as undefined if we reach maturityDate
      if (nextPeriod <= DateTime.fromJSDate(maturityDate)) {
        nextPaymentDueDate = nextPeriod.toJSDate();
      }
    }
  }

  return nextPaymentDueDate;
}

/**
 * If statement is generated on the 18th, add 1 month
 * If it's generated on the 1st, do not add a month since it's already in the new month
 */
export function getNextPaymentDateByStatementDate(nextStatementDate: Date): Date {
  const nextStatementDT = DateTime.fromJSDate(nextStatementDate);
  if (nextStatementDT.day === 1) {
    return nextStatementDT.startOf('month').toJSDate();
  }
  return nextStatementDT.plus({ months: 1 }).startOf('month').toJSDate();
}

export const getPaymentsDueWhere = (
  snapshot: Pick<LoanSnapshotConfig, 'loanStatus' | 'transferData' | 'paymentsDue'>,
  where: {
    isDueToNextServicer?: boolean;
    isFulfilled?: boolean;
    isLate?: boolean;
    hasAmountRemaining?: boolean;
    hasWorkoutPlan?: boolean;
    isDeferred?: boolean;
    wentPastGrace?: boolean;
    filterFn?: (paymentDue: PaymentDue) => boolean;
  } = {},
) =>
  snapshot.paymentsDue.filter(
    (pd) =>
      (where.isDueToNextServicer != null
        ? isPaymentDueToNextServicer(pd, snapshot) === where.isDueToNextServicer
        : true) &&
      (where.isFulfilled != null ? where.isFulfilled === pd.status.fulfilled : true) &&
      (where.isLate != null ? where.isLate === pd.status.isLate : true) &&
      (where.hasAmountRemaining != null ? where.hasAmountRemaining === Boolean(pd.status.amountRemaining) : true) &&
      (where.hasWorkoutPlan != null ? where.hasWorkoutPlan === Boolean(pd.status.dWorkoutPlan) : true) &&
      (where.isDeferred != null
        ? where.isDeferred === ['paymentDeferment', 'paymentDeferral'].includes(pd.status.dWorkoutPlan?.type ?? '')
        : true) &&
      (where.wentPastGrace != null ? where.wentPastGrace === pd.wentPastGrace : true) &&
      (where.filterFn ? where.filterFn(pd) : true),
  );

export const isPaymentDueToNextServicer = (
  paymentDue: PaymentDue,
  snapshotConfig: Pick<LoanSnapshotConfig, 'transferData' | 'loanStatus'>,
): boolean => {
  const { loanStatus, transferData } = snapshotConfig;
  const loanIsTransferring = loanStatus === 'transferred' || loanStatus === 'pendingTransfer';

  // Return false if this loan isn't transferring
  if (!loanIsTransferring || !transferData) {
    return false;
  }

  if (transferData.nextServicerCollectsLatePayments && isPaymentLate(paymentDue.paymentDate)) {
    const effectiveDate = transferData.legalTransferEffectiveDate ?? transferData.transferEffectiveDate;
    return new Date() >= DateTime.fromJSDate(effectiveDate).startOf('day').toJSDate();
  }

  // Orphaned draws are still collectable even if after transfer since the original servicer gets the interest
  if (paymentDue.orphanedDrawsOnly) {
    return false;
  }

  // Is this payment due AFTER or ON the transfer date?
  return (
    DateTime.fromJSDate(paymentDue.paymentDate).startOf('day') >=
    DateTime.fromJSDate(transferData.transferEffectiveDate).startOf('day')
  );
};

export const isPaymentLate = (paymentDate: Date): boolean => {
  const today = DateTime.now().startOf('day');
  const paymentDateToCompare = DateTime.fromJSDate(paymentDate).startOf('day');
  return today > paymentDateToCompare;
};

export const getDaysPastDueAsOfDate = (
  loanSnapshot: Pick<LoanSnapshotConfig, 'loanStatus' | 'transferData' | 'paymentsDue' | 'workoutPlans'>,
  targetDate: Date,
  timezone: Temporal.TimeZone['id'] = 'UTC',
) => {
  const pastDueDates = getOverduePaymentsDueAsOfDate(loanSnapshot, targetDate, timezone, false).map((pd) =>
    DateTime.fromJSDate(pd.paymentDate, { zone: timezone }),
  );

  if (!pastDueDates.length) return 0;

  const { days } = DateTime.fromJSDate(targetDate, { zone: timezone })
    .startOf('day')
    .diff(DateTime.min(...pastDueDates), ['days']);
  return Math.max(Math.floor(days), 0);
};

export const getOverduePaymentsDueAsOfDate = (
  loanSnapshot: Pick<LoanSnapshotConfig, 'loanStatus' | 'transferData' | 'paymentsDue' | 'workoutPlans'>,
  targetDate: Date,
  timezone?: Temporal.TimeZone['id'],
  includeDeferred?: boolean,
) => {
  const date = DateTime.fromJSDate(targetDate, { zone: timezone }).endOf('day').toJSDate();
  return getPaymentsDueWhere(loanSnapshot, {
    isDueToNextServicer: false,
    filterFn: (pd) =>
      pd.paymentDate <= date &&
      (!pd.status.fulfilledAt || date < pd.status.fulfilledAt) &&
      // Pre-petition arrears are cured by the trustee, not borrower delinquency — exclude them
      // (like deferred statements) unless the caller explicitly wants the deferred set too.
      (includeDeferred
        ? true
        : !pd.status.isPrePetition &&
          !['flexModification', 'paymentDeferment', 'paymentDeferral'].includes(
            getActiveOrCompletedWorkoutPlanForPaymentDue(loanSnapshot.workoutPlans, pd, date)?.type ?? '',
          )),
  });
};
